import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/api';

interface TimezoneResponse {
  timezone: string;
  user_id: number;
}

export const useUserTimezone = () => {
  const [timezone, setTimezone] = useState<string>('America/Los_Angeles');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const { token, isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchTimezone = async () => {
      if (!isAuthenticated || !token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const API_URL = getApiUrl();
        const response = await fetch(`${API_URL}/auth/timezone`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch timezone: ${response.statusText}`);
        }

        const data: TimezoneResponse = await response.json();
        setTimezone(data.timezone);
      } catch (err) {
        console.error('Error fetching user timezone:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch timezone');
        // Fallback to default timezone
        setTimezone('America/Los_Angeles');
      } finally {
        setLoading(false);
      }
    };

    fetchTimezone();
  }, [isAuthenticated, token, refreshTrigger]);

  const refresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return { timezone, loading, error, refresh };
};
